import { differenceInYears, isValid, parseISO } from 'date-fns';

import { cn } from '@/lib/utils';
import Header from './card-header';

export type PersonalInformationCandidate = {
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  email?: string | null;
  birthDate?: string | null;
  city?: string | null;
  street?: string | null;
  postalCode?: string | null;
  resumeUrl?: string | null;
  hubspotUrl?: string | null;
};

type PersonalInformationProps = {
  candidate?: PersonalInformationCandidate | null;
  className?: string;
  emptyStateMessage?: string;
};

const personalInformationFields: Array<{
  key: keyof PersonalInformationCandidate;
  label: string;
}> = [
    { key: 'firstName', label: 'Voornaam' },
    { key: 'lastName', label: 'Achternaam' },
    { key: 'phone', label: 'Telefoonnummer' },
    { key: 'email', label: 'Emailadres' },
    { key: 'birthDate', label: 'Geboortedatum' },
    { key: 'city', label: 'Woonplaats' },
    { key: 'street', label: 'Straatnaam' },
    { key: 'postalCode', label: 'Postcode' },
  ];

const defaultEmptyStateMessage = 'Selecteer een kandidaat om de gegevens te bekijken.';

const disabledInputClassName =
  'w-full rounded-md border border-slate-200 bg-slate-500/10 px-3 py-2 text-sm transition placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-1';

const labelClassName = 'text-xs tracking-wide text-slate-500';

const fieldWrapperClassName = 'flex flex-col gap-1.5';

const containerClassName = 'grid grid-cols-1 gap-4 md:grid-cols-2';

const sectionClassName = 'rounded-xl flex flex-col gap-4 border border-slate-200';

// wtf gebeurd hier boven met classnames @Anthony
const parseBirthDateValue = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  try {
    const isoParsedDate = parseISO(trimmedValue);
    if (isValid(isoParsedDate)) {
      return isoParsedDate;
    }
  } catch (error) {
    // Ignore parsing errors and fallback to Date constructor.
  }

  const fallbackDate = new Date(trimmedValue);
  return isValid(fallbackDate) ? fallbackDate : null;
};

const getAgeFromBirthDate = (birthDate?: string | null): number | null => {
  const parsedBirthDate = parseBirthDateValue(birthDate);
  if (!parsedBirthDate) {
    return null;
  }

  const age = differenceInYears(new Date(), parsedBirthDate);
  return Number.isFinite(age) && age >= 0 ? age : null;
};

const PersonalInformation = ({
  candidate,
  className,
  emptyStateMessage = defaultEmptyStateMessage,
}: PersonalInformationProps) => {
  const hasCandidate = Boolean(candidate);

  return (
    <section className={cn(sectionClassName, className)}>
      <Header
        title="Persoonlijke informatie"
        subtitle="Overzicht van de gegevens zoals aangeleverd door de kandidaat."
      />
      {hasCandidate ? (
        <div className={containerClassName}>
          {personalInformationFields.map(({ key, label }) => {
            if (key === 'birthDate') {
              const birthDateValue = candidate?.birthDate ? String(candidate.birthDate) : '';
              const age = getAgeFromBirthDate(candidate?.birthDate);
              const ageValue = typeof age === 'number' ? String(age) : '';

              return (
                <div key={key as string} className='flex flex-row '>
                  <label className={`${fieldWrapperClassName} flex-1`}>
                    <span className={labelClassName}>{label}</span>
                    <input
                      type="text"
                      value={birthDateValue}
                      placeholder="-"
                      disabled
                      className={`${disabledInputClassName} rounded-r-none`}
                    />
                  </label>
                  <label className={`${fieldWrapperClassName} flex-1`}>
                    <span className={labelClassName}>Leeftijd</span>
                    <input
                      type="text"
                      value={ageValue}
                      placeholder="-"
                      disabled
                      className={`${disabledInputClassName} rounded-l-none border-l-0`}
                    />
                  </label>
                </div>
              );
            }

            return (
              <label key={key as string} className={fieldWrapperClassName}>
                <span className={labelClassName}>{label}</span>
                <input
                  type="text"
                  value={candidate?.[key] ? String(candidate[key]) : ''}
                  placeholder="-"
                  disabled
                  className={disabledInputClassName}
                />
              </label>
            );
          })}
        </div>
      ) : (
        <div className="rounded-md border border-dashed border-border px-6 py-10 text-sm text-slate-500">
          {emptyStateMessage}
        </div>
      )}
    </section>
  );
};

export default PersonalInformation;
